import {
  DEVICE_TYPE_CODE_BY_NAME,
  DEVICE_TYPE_NAME_BY_CODE,
  DeviceTypeCodes,
  DeviceTypeName,
  PLANT_MEASUREMENT_CODE_BY_NAME,
} from './devices.schemas.ts';
import {
  createDeviceEventMetadata,
  createDeviceTypeMetadata,
  DeviceTypeMetadata,
} from './devices.utils';

export const PLANT_FIELDS_METADATA = {
  [PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status']]: createDeviceTypeMetadata([
    { id: -1, name: 'Disconnected', unit: '', decimals: 0 },
    { id: 0, name: 'No data', unit: '', decimals: 0 },
    { id: 1, name: 'Connected', unit: '', decimals: 0 },
  ] as const),
};

export const DEVICE_FIELDS_METADATA = {
  [DEVICE_TYPE_CODE_BY_NAME.Inverter]: createDeviceTypeMetadata([
    { id: 1, name: 'ModBus Status', unit: '', decimals: 2 },
    { id: 320, name: 'Inverter Mode', unit: '', decimals: 2 },
    { id: 321, name: 'Input Current Limit', unit: 'A', decimals: 2 },
    { id: 322, name: 'Set P', unit: 'kW', decimals: 2 },
  ] as const),
  [DEVICE_TYPE_CODE_BY_NAME.BMS]: createDeviceTypeMetadata([
    { id: 1, name: 'ModBus Status', unit: '', decimals: 2 },
    { id: 10, name: 'Status', unit: '', decimals: 2 },
    { id: 11, name: 'Error Bits', unit: '', decimals: 2 },
    { id: 12, name: 'Warn Bits', unit: '', decimals: 2 },
    { id: 16, name: 'State of Charge of Installed Strings', unit: '%', decimals: 2 },
    { id: 17, name: 'State of Charge of Connected Strings', unit: '%', decimals: 2 },
    { id: 19, name: 'Maximum Cell Voltage Difference', unit: 'V', decimals: 3 },
    { id: 20, name: 'Voltage', unit: 'V', decimals: 2 },
    { id: 21, name: 'Current', unit: 'A', decimals: 2 },
    { id: 22, name: 'Power', unit: 'kW', decimals: 2 },
    { id: 23, name: 'Discharge Power', unit: 'kW', decimals: 2 },
    { id: 24, name: 'Charge Power', unit: 'kW', decimals: 2 },
    { id: 29, name: 'State of Charge Estimation', unit: '%', decimals: 2 },
    { id: 30, name: 'State of Charge', unit: '%', decimals: 2 },
    { id: 31, name: 'Status Error', unit: '', decimals: 2 },
    { id: 32, name: 'Status Charge', unit: '', decimals: 2 },
    { id: 33, name: 'Status Discharge', unit: '', decimals: 2 },
    { id: 34, name: 'Status Idle', unit: '', decimals: 2 },
    { id: 41, name: 'Maximum Discharge Current', unit: 'A', decimals: 2 },
    { id: 42, name: 'Maximum Charge Current', unit: 'A', decimals: 2 },
    { id: 80, name: 'Average Temperature', unit: '°C', decimals: 2 },
    { id: 81, name: 'Minimum Temperature', unit: '°C', decimals: 2 },
    { id: 82, name: 'Maximum Temperature', unit: '°C', decimals: 2 },
    { id: 83, name: 'Minimum Cell Voltage', unit: 'V', decimals: 3 },
    { id: 84, name: 'Maximum Cell Voltage', unit: 'V', decimals: 3 },
    { id: 85, name: 'Average Cell Voltage', unit: 'V', decimals: 3 },
    { id: 133, name: 'Min Cell Voltage', unit: 'V', decimals: 3 },
    { id: 134, name: 'Max Cell Voltage', unit: 'V', decimals: 3 },
    { id: 136, name: 'Strings Installed', unit: '', decimals: 2 },
    { id: 137, name: 'Strings Connected', unit: '', decimals: 2 },
    { id: 145, name: 'Min String Voltage', unit: 'V', decimals: 2 },
    { id: 147, name: 'Max String Voltage', unit: 'V', decimals: 2 },
    { id: 155, name: 'Balancing Cell Count', unit: '', decimals: 2 },
    { id: 208, name: 'Voltage of all Strings', unit: 'V', decimals: 2 },
    { id: 278, name: 'Energy Throughput', unit: 'kWh', decimals: 2 },
    { id: 279, name: 'Charged Energy', unit: 'kWh', decimals: 2 },
    { id: 283, name: 'Discharged Energy', unit: 'kWh', decimals: 2 },
    { id: 290, name: 'Total Charged Capacity', unit: 'Ah', decimals: 2 },
    { id: 291, name: 'Total Discharged Capacity', unit: 'Ah', decimals: 2 },
    { id: 292, name: 'Capacity Throughput', unit: 'Ah', decimals: 2 },
    { id: 293, name: 'Session Charged Capacity', unit: 'Ah', decimals: 2 },
    { id: 294, name: 'Session Discharged Capacity', unit: 'Ah', decimals: 2 },
    { id: 1020, name: 'Remaining Charge Energy', unit: 'kWh', decimals: 2 },
    { id: 1021, name: 'Remaining Discharge Energy', unit: 'kWh', decimals: 2 },
    { id: 1026, name: 'State of Health', unit: '%', decimals: 2 },
    { id: 1048, name: 'Serial Number', unit: '', decimals: 2 },
    { id: 5001, name: 'Event code', unit: '', decimals: 2 },
  ] as const),
  [DEVICE_TYPE_CODE_BY_NAME.SmartMeter]: createDeviceTypeMetadata([
    { id: 200, name: 'Voltage L1-N', unit: 'V', decimals: 2 },
    { id: 201, name: 'Voltage L2-N', unit: 'V', decimals: 2 },
    { id: 202, name: 'Voltage L3-N', unit: 'V', decimals: 2 },
    { id: 203, name: 'Voltage L1-L2', unit: 'V', decimals: 2 },
    { id: 204, name: 'Voltage L2-L3', unit: 'V', decimals: 2 },
    { id: 205, name: 'Voltage L3-L1', unit: 'V', decimals: 2 },
    { id: 210, name: 'Current L1', unit: 'A', decimals: 2 },
    { id: 211, name: 'Current L2', unit: 'A', decimals: 2 },
    { id: 212, name: 'Current L3', unit: 'A', decimals: 2 },
    { id: 213, name: 'Neutral Current', unit: 'A', decimals: 2 },
    { id: 220, name: 'Real Power L1', unit: 'kW', decimals: 2 },
    { id: 221, name: 'Real Power L2', unit: 'kW', decimals: 2 },
    { id: 222, name: 'Real Power L3', unit: 'kW', decimals: 2 },
    { id: 100, name: 'Total Real Power', unit: 'kW', decimals: 2 },
    { id: 230, name: 'Apparent Power L1', unit: 'kVA', decimals: 2 },
    { id: 231, name: 'Apparent Power L2', unit: 'kVA', decimals: 2 },
    { id: 232, name: 'Apparent Power L3', unit: 'kVA', decimals: 2 },
    { id: 233, name: 'Total Apparent Power', unit: 'kVA', decimals: 2 },
    { id: 240, name: 'Reactive Power L1', unit: 'kvar', decimals: 2 },
    { id: 241, name: 'Reactive Power L2', unit: 'kvar', decimals: 2 },
    { id: 242, name: 'Reactive Power L3', unit: 'kvar', decimals: 2 },
    { id: 243, name: 'Total Reactive Power', unit: 'kvar', decimals: 2 },
    { id: 250, name: 'Power Factor L1', unit: '', decimals: 2 },
    { id: 251, name: 'Power Factor L2', unit: '', decimals: 2 },
    { id: 252, name: 'Power Factor L3', unit: '', decimals: 2 },
    { id: 260, name: 'Grid Frequency', unit: 'Hz', decimals: 2 },
    { id: 270, name: 'Active Energy Consumed L1', unit: 'kWh', decimals: 2 },
    { id: 271, name: 'Active Energy Consumed L2', unit: 'kWh', decimals: 2 },
    { id: 272, name: 'Active Energy Consumed L3', unit: 'kWh', decimals: 2 },
    { id: 111, name: 'Total Import Active Energy', unit: 'kWh', decimals: 2 },
    { id: 275, name: 'Active Energy Delivered L1', unit: 'kWh', decimals: 2 },
    { id: 276, name: 'Active Energy Delivered L2', unit: 'kWh', decimals: 2 },
    { id: 277, name: 'Active Energy Delivered L3', unit: 'kWh', decimals: 2 },
    { id: 112, name: 'Total Export Active Energy', unit: 'kWh', decimals: 2 },
    { id: 280, name: 'Reactive Energy Inductive L1', unit: 'kvarh', decimals: 2 },
    { id: 281, name: 'Reactive Energy Inductive L2', unit: 'kvarh', decimals: 2 },
    { id: 282, name: 'Reactive Energy Inductive L3', unit: 'kvarh', decimals: 2 },
    { id: 283, name: 'Total Reactive Energy Inductive', unit: 'kvarh', decimals: 2 },
    { id: 284, name: 'Reactive Energy Capacitive L1', unit: 'kvarh', decimals: 2 },
    { id: 285, name: 'Reactive Energy Capacitive L2', unit: 'kvarh', decimals: 2 },
    { id: 286, name: 'Reactive Energy Capacitive L3', unit: 'kvarh', decimals: 2 },
    { id: 287, name: 'Total Reactive Energy Capacitive', unit: 'kvarh', decimals: 2 },
    { id: 288, name: 'Total Apparent Energy', unit: 'kVAh', decimals: 2 },
    { id: 290, name: 'THD Voltage L1', unit: '%', decimals: 2 },
    { id: 291, name: 'THD Voltage L2', unit: '%', decimals: 2 },
    { id: 292, name: 'THD Voltage L3', unit: '%', decimals: 2 },
    { id: 293, name: 'THD Current L1', unit: '%', decimals: 2 },
    { id: 294, name: 'THD Current L2', unit: '%', decimals: 2 },
    { id: 295, name: 'THD Current L3', unit: '%', decimals: 2 },
    { id: 300, name: 'Voltage L1-N Fast', unit: 'V', decimals: 2 },
    { id: 301, name: 'Voltage L2-N Fast', unit: 'V', decimals: 2 },
    { id: 302, name: 'Voltage L3-N Fast', unit: 'V', decimals: 2 },
    { id: 310, name: 'Current L1 Fast', unit: 'A', decimals: 2 },
    { id: 311, name: 'Current L2 Fast', unit: 'A', decimals: 2 },
    { id: 312, name: 'Current L3 Fast', unit: 'A', decimals: 2 },
    { id: 313, name: 'Neutral Current Fast', unit: 'A', decimals: 2 },
    { id: 320, name: 'Frequency Fast', unit: 'Hz', decimals: 2 },
    { id: 330, name: 'Voltage Unbalance', unit: '%', decimals: 2 },
    { id: 331, name: 'Current Unbalance', unit: '%', decimals: 2 },
    { id: 340, name: 'Total Active Energy', unit: 'kWh', decimals: 2 },
    { id: 341, name: 'Total Reactive Energy', unit: 'kvarh', decimals: 2 },
    { id: 342, name: 'Total Apparent Energy', unit: 'kVAh', decimals: 2 },
  ] as const),
  [DEVICE_TYPE_CODE_BY_NAME.SmartLogger]: createDeviceTypeMetadata([
    { id: 1, name: 'ModBus Status', unit: '', decimals: 2 },
    { id: 100, name: 'Grid', unit: 'kW', decimals: 2 },
    { id: 101, name: 'Grid Consumption', unit: 'kW', decimals: 2 },
    { id: 102, name: 'Fed To Grid', unit: 'kW', decimals: 2 },
    { id: 103, name: 'Consumption', unit: 'kW', decimals: 2 },
    { id: 104, name: 'Self Consumption', unit: 'kW', decimals: 2 },
    { id: 202, name: 'Battery Voltage', unit: 'V', decimals: 2 },
    { id: 203, name: 'Battery Power', unit: 'kW', decimals: 2 },
    { id: 204, name: 'Battery Current', unit: 'A', decimals: 2 },
    { id: 260, name: 'Phase A Voltage', unit: 'V', decimals: 2 },
    { id: 262, name: 'Phase B Voltage', unit: 'V', decimals: 2 },
    { id: 264, name: 'Phase C Voltage', unit: 'V', decimals: 2 },
    { id: 500, name: 'Station power consumption', unit: 'kW', decimals: 2 },
    { id: 501, name: 'Station peak power consumption', unit: 'kW', decimals: 2 },
    { id: 502, name: 'Voltage between phase L1 and ground', unit: 'V', decimals: 2 },
    { id: 503, name: 'Voltage between phases L1 & L2', unit: 'V', decimals: 2 },
    { id: 504, name: 'Voltage between phases L3 & L1', unit: 'V', decimals: 2 },
    { id: 505, name: 'Voltage between phases L2 & L3', unit: 'V', decimals: 2 },
    { id: 518, name: 'Grid Frequency', unit: 'Hz', decimals: 2 },
    { id: 620, name: 'Assist Power', unit: 'kW', decimals: 2 },
    { id: 703, name: 'Inverter board temperature', unit: '℃', decimals: 2 },
    {
      id: 707,
      name: 'Highest temperature among all phases of the IGBT',
      unit: '℃',
      decimals: 2,
    },
    { id: 810, name: 'Active Power', unit: 'kW', decimals: 2 },
    { id: 812, name: 'Reactive Power', unit: 'kvar', decimals: 2 },
    { id: 814, name: 'Apparent Power', unit: 'kVA', decimals: 2 },
    { id: 816, name: 'Total Import Active Energy', unit: 'kWh', decimals: 2 },
    { id: 818, name: 'Total Import (Inductive) Reactive Energy', unit: 'kvarh', decimals: 2 },
    { id: 820, name: 'Total Export (Capacitive) Reactive Energy', unit: 'kvarh', decimals: 2 },
    { id: 822, name: 'Total Active Energy', unit: 'kWh', decimals: 2 },
    { id: 824, name: 'Total Reactive Energy', unit: 'kvarh', decimals: 2 },
    { id: 826, name: 'Total Apparent Energy', unit: 'kVAh', decimals: 2 },
    { id: 40550, name: 'CO2 Reduction', unit: 'kg', decimals: 2 },
    { id: 40566, name: 'Plant Status', unit: '', decimals: 2 },
    { id: 40525, name: 'Production', unit: 'kW', decimals: 2 },
    { id: 40562, name: 'Production Today', unit: 'kWh', decimals: 2 },
    { id: 40560, name: 'Production Total', unit: 'kWh', decimals: 2 },
  ] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Dongle]: createDeviceTypeMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Logger]: createDeviceTypeMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.PQConnect]: createDeviceTypeMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Battery]: createDeviceTypeMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Sensor]: createDeviceTypeMetadata([
    { id: 100, name: 'Room Temperature', unit: '℃', decimals: 2 },
    { id: 101, name: 'Room Humidity', unit: '%', decimals: 2 },
  ] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Unknown]: createDeviceTypeMetadata([] as const),
} as const;

export const DEVICE_EVENTS_METADATA = {
  [DEVICE_TYPE_CODE_BY_NAME.Inverter]: createDeviceEventMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.BMS]: createDeviceEventMetadata([
    { id: 102, name: 'EVENT_CODE_RESET', description: '', type: 'Info' },
    { id: 110, name: 'EVENT_CODE_PRECHARGE', description: '', type: 'Info' },
    { id: 122, name: 'EVENT_CODE_EVENT_LOG_CLEARED', description: '', type: 'Info' },
    { id: 701, name: 'EVENT_CODE_HB_TIMEOUT', description: '', type: 'Info' },
    { id: 934, name: 'EVENT_CODE_PRECHARGE_ERROR', description: '', type: 'Warning' },
  ] as const),
  [DEVICE_TYPE_CODE_BY_NAME.SmartMeter]: createDeviceEventMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.SmartLogger]: createDeviceEventMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Dongle]: createDeviceEventMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Logger]: createDeviceEventMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.PQConnect]: createDeviceEventMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Battery]: createDeviceEventMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Sensor]: createDeviceEventMetadata([] as const),
  [DEVICE_TYPE_CODE_BY_NAME.Unknown]: createDeviceEventMetadata([] as const),
};

//
// //
// //
// //
//

export function getDeviceTypeIdByName<T extends DeviceTypeName>(deviceName: T) {
  if (deviceName in DEVICE_TYPE_CODE_BY_NAME) {
    return DEVICE_TYPE_CODE_BY_NAME[String(deviceName) as T];
  }

  throw new Error(`No device id with the name ${String(deviceName)} found`);
}

export function getDeviceTypeNameByCode(deviceId: DeviceTypeCodes) {
  if (deviceId in DEVICE_TYPE_NAME_BY_CODE) {
    return DEVICE_TYPE_NAME_BY_CODE[Number(deviceId) as DeviceTypeCodes];
  }

  throw new Error(`No device name with the id ${String(deviceId)} found`);
}

//
// //
// //
// //
//

export type FieldNamesForDevice<DevTypeName extends DeviceTypeName> =
  keyof (typeof DEVICE_FIELDS_METADATA)[(typeof DEVICE_TYPE_CODE_BY_NAME)[DevTypeName]]['parametersByName'];

export type FieldIdsForDevice<DevTypeName extends DeviceTypeName> =
  keyof (typeof DEVICE_FIELDS_METADATA)[(typeof DEVICE_TYPE_CODE_BY_NAME)[DevTypeName]]['parametersByCode'];

export function getDeviceFieldByName<
  T extends DeviceTypeName,
  P extends FieldNamesForDevice<T>,
>(deviceName: T, fieldName: P) {
  const deviceTypeId = DEVICE_TYPE_CODE_BY_NAME[String(deviceName) as T];
  const deviceMetadata = DEVICE_FIELDS_METADATA[Number(deviceTypeId) as DeviceTypeCodes];

  const parameters = deviceMetadata.parametersByName as {
    [key in P]: { id: number; name: string; unit: string; decimals: number };
  };

  if (fieldName in parameters) {
    return parameters[
      fieldName as P
    ] as (typeof DEVICE_FIELDS_METADATA)[(typeof DEVICE_TYPE_CODE_BY_NAME)[T]]['parametersByName'][P];
  }

  return undefined;
}

export function getDeviceFieldByFieldId<
  T extends DeviceTypeName,
  P extends FieldIdsForDevice<T> | number,
  R extends { id: number; name: string; unit: string; decimals: number },
>(deviceName: T, fieldId: P) {
  const deviceTypeId = DEVICE_TYPE_CODE_BY_NAME[String(deviceName) as T];
  const deviceMetadata = DEVICE_FIELDS_METADATA[Number(deviceTypeId) as DeviceTypeCodes];

  const parameters = deviceMetadata.parametersByCode as {
    [key in P]: R;
  };

  if (fieldId in parameters) {
    return parameters[fieldId as P] as P extends FieldIdsForDevice<T>
      ? (typeof DEVICE_FIELDS_METADATA)[(typeof DEVICE_TYPE_CODE_BY_NAME)[T]]['parametersByCode'][P]
      : R | undefined;
  }

  return undefined;
}

//
// //
// //
// //
//

export type EventNamesForDevice<DevTypeName extends DeviceTypeName> =
  keyof (typeof DEVICE_EVENTS_METADATA)[(typeof DEVICE_TYPE_CODE_BY_NAME)[DevTypeName]]['parametersByName'];

export type EventCodesForDevice<DevTypeName extends DeviceTypeName> =
  keyof (typeof DEVICE_EVENTS_METADATA)[(typeof DEVICE_TYPE_CODE_BY_NAME)[DevTypeName]]['parametersByCode'];

export type FieldNamesForDeviceTyeCode<DevTypeCode extends DeviceTypeCodes> =
  keyof (typeof DEVICE_FIELDS_METADATA)[DevTypeCode]['parametersByName'];

export type FieldIdsForDeviceTypeCode<DevTypeCode extends DeviceTypeCodes> =
  keyof (typeof DEVICE_FIELDS_METADATA)[DevTypeCode]['parametersByCode'];

export function getDeviceEventByName<
  T extends DeviceTypeName,
  P extends EventNamesForDevice<T>,
>(deviceTypeName: T, deviceFieldName: P) {
  const deviceTypeId = DEVICE_TYPE_CODE_BY_NAME[String(deviceTypeName) as T];
  const deviceMetadata = DEVICE_EVENTS_METADATA[Number(deviceTypeId) as DeviceTypeCodes];

  const parameters = deviceMetadata.parametersByName;

  if (deviceFieldName in parameters) {
    return parameters[
      deviceFieldName as keyof typeof parameters
    ] as (typeof DEVICE_EVENTS_METADATA)[(typeof DEVICE_TYPE_CODE_BY_NAME)[T]]['parametersByName'][P];
  }

  throw new Error(
    `No field with the name ${String(deviceFieldName)} found in ${String(deviceTypeName)} device`
  );
}

export function getDeviceEventById<
  T extends DeviceTypeCodes,
  P extends EventCodesForDevice<(typeof DEVICE_TYPE_NAME_BY_CODE)[T]>,
>(
  deviceTypeCode: T,
  deviceFieldCode: P
): (typeof DEVICE_EVENTS_METADATA)[T]['parametersByCode'][P] {
  const deviceMetadata = DEVICE_EVENTS_METADATA[Number(deviceTypeCode) as DeviceTypeCodes];

  const parameters = deviceMetadata.parametersByCode;

  if (deviceFieldCode in parameters) {
    return parameters[
      deviceFieldCode as keyof typeof parameters
    ] as (typeof DEVICE_EVENTS_METADATA)[T]['parametersByName'][P];
  }

  throw new Error(
    `No field with the code ${String(deviceTypeCode)} found in ${String(deviceFieldCode)} device`
  );
}

//
// //
// //
// //
//

export function getFieldName<T extends DeviceTypeName>(deviceName: T, fieldId: number) {
  const DEVICE_TYPE_BY_ID: DeviceTypeCodes = DEVICE_TYPE_CODE_BY_NAME[deviceName];
  const DEVICE_PARAMETERS = DEVICE_FIELDS_METADATA[DEVICE_TYPE_BY_ID].parametersByCode;

  if (fieldId in DEVICE_PARAMETERS) {
    // @ts-expect-error -- dynamic key access on typed object
    return DEVICE_PARAMETERS[fieldId].name;
  }

  return fieldId;
}

export function getFieldMetadata<T extends DeviceTypeName>(deviceName: T, fieldId: number) {
  const DEVICE_TYPE_BY_ID: DeviceTypeCodes = DEVICE_TYPE_CODE_BY_NAME[deviceName];
  const DEVICE_PARAMETERS = DEVICE_FIELDS_METADATA[DEVICE_TYPE_BY_ID].parametersByCode;

  if (fieldId in DEVICE_PARAMETERS) {
    // @ts-expect-error -- dynamic key access on typed object
    return DEVICE_PARAMETERS[fieldId];
  }

  return undefined;
}

export function getFieldUnit<T extends DeviceTypeName>(deviceName: T, fieldId: number) {
  const DEVICE_TYPE_BY_ID: DeviceTypeCodes = DEVICE_TYPE_CODE_BY_NAME[deviceName];
  const DEVICE_PARAMETERS = DEVICE_FIELDS_METADATA[DEVICE_TYPE_BY_ID].parametersByCode;

  if (fieldId in DEVICE_PARAMETERS) {
    // @ts-expect-error -- dynamic key access on typed object
    return DEVICE_PARAMETERS[fieldId].unit;
  }

  return '';
}

//
//

export function getDeviceFieldByCode<
  T extends DeviceTypeCodes,
  P extends FieldIdsForDeviceTypeCode<T> | number,
  R extends DeviceTypeMetadata,
>(deviceTypeId: T, fieldId: P) {
  if (!deviceTypeId || !fieldId) return;

  const deviceMetadata = DEVICE_FIELDS_METADATA[Number(deviceTypeId) as DeviceTypeCodes];

  const parameters = deviceMetadata.parametersByCode as {
    [key in P]: R;
  };

  if (fieldId in parameters) {
    return parameters[fieldId as P] as P extends FieldIdsForDeviceTypeCode<T>
      ? (typeof DEVICE_FIELDS_METADATA)[T]['parametersByCode'][P]
      : R | undefined;
  }

  return;
}
